import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Image, BookOpen, Mail, ArrowRight, DollarSign, CalendarCheck } from 'lucide-react';
import { AdminPageHeader, AdminSurface } from '@/components/admin/AdminPrimitives';

export default function AdminDashboard() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canViewGallery = hasPermission('gallery');
  const canViewBlog = hasPermission('blog');
  const canViewMessages = hasPermission('messages');
  const canViewActivityRegistrations = hasPermission('activity_registrations');
  const canManageDonations = hasPermission('donations');
  const canViewDonationStats = isSuperAdmin();
  const [stats, setStats] = useState({ gallery: 0, blog: 0, messages: 0, unread: 0, donations: 0, donationAmount: 0, activityRegistrations: 0, newActivityRegistrations: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await api.get('/admin/dashboard-stats');
        setStats(data);
      } catch {}
    };
    fetchStats();
  }, [canViewActivityRegistrations, canViewBlog, canViewDonationStats, canViewGallery, canViewMessages]);

  const cards = [
    { title: 'Gallery Images', value: stats.gallery, icon: Image, link: '/admin/gallery', color: 'bg-blue-50 text-[#1E4FA1]', show: canViewGallery },
    { title: 'Blog Posts', value: stats.blog, icon: BookOpen, link: '/admin/blog', color: 'bg-emerald-50 text-emerald-600', show: canViewBlog },
    { title: 'Messages', value: stats.messages, icon: Mail, link: '/admin/messages', color: 'bg-amber-50 text-amber-600', badge: stats.unread, show: canViewMessages },
    { title: 'Registrations', value: stats.activityRegistrations, icon: CalendarCheck, link: '/admin/activity-registrations', color: 'bg-sky-50 text-sky-600', badge: stats.newActivityRegistrations, show: canViewActivityRegistrations },
    { title: 'Donations', value: `$${stats.donationAmount.toFixed(2)}`, icon: DollarSign, link: '/admin/donations', color: 'bg-green-50 text-green-600', show: canViewDonationStats },
  ].filter(c => c.show);

  return (
    <div data-testid="admin-dashboard">
      <AdminPageHeader
        eyebrow="Ministry Operations"
        title="Dashboard"
        description="A calm overview of content, conversations, registrations, and giving activity."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.title}
              to={card.link}
              data-testid={`dashboard-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="mscm-admin-surface mscm-admin-card-link block p-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/30"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-11 h-11 ${card.color} rounded-[8px] flex items-center justify-center shadow-[inset_0_0_0_1px_rgba(255,255,255,0.65)]`}>
                  <Icon size={20} />
                </div>
                {card.badge > 0 && (
                  <span className="bg-[#EF4444] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {card.badge}
                  </span>
                )}
              </div>
              <p className="mscm-admin-stat-value text-2xl font-bold">{card.value}</p>
              <p className="mt-1 text-sm font-medium text-[#64748B]">{card.title}</p>
            </Link>
          );
        })}
      </div>

      {/* Quick Links */}
      <AdminSurface className="p-6">
        <h2 className="text-xl font-bold text-[#0F172A] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Quick Actions
        </h2>
        <p className="mb-5 text-sm text-[#64748B]">Jump directly into the most common ministry updates.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: 'Edit Home Page Content', to: '/admin/content', show: hasPermission('content') },
            { label: 'Manage Gallery Images', to: '/admin/gallery', show: canViewGallery },
            { label: 'Create Blog Post', to: '/admin/blog', show: canViewBlog },
            { label: 'Update Founder Info', to: '/admin/founder', show: hasPermission('founder') },
            { label: 'Edit Contact Information', to: '/admin/contact', show: hasPermission('contact') },
            { label: 'View Messages', to: '/admin/messages', show: canViewMessages },
            { label: 'View Activity Registrations', to: '/admin/activity-registrations', show: canViewActivityRegistrations },
            { label: 'Manage Donation Methods', to: '/admin/donations', show: canManageDonations },
          ].filter(action => action.show).map(action => (
            <Link
              key={action.to}
              to={action.to}
              data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="mscm-admin-action-link flex min-h-12 items-center justify-between gap-3 rounded-[8px] border border-[#E2E8F0]/70 bg-[#F8FBFF] px-4 py-3 text-sm font-semibold text-[#475569] hover:bg-[#EBF2FA] hover:text-[#1E4FA1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4FA1]/30"
            >
              {action.label}
              <ArrowRight size={14} />
            </Link>
          ))}
        </div>
      </AdminSurface>
    </div>
  );
}
